import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { returnToFromSearch } from '../utils/safeReturnTo';
import { defaultAuthenticatedHomePath } from '../utils/hubNavigation';
import { initializePwa } from '../utils/pwa';
import { registerSessionInvalidHandler, resetSessionInvalidState } from '../utils/sessionAuth';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const INTAKE_EXEMPT_PATH_PATTERN = /^\/fitness-profiles\/\d+\/complete-intake$/;

function intakeRedirectPath(activeFitnessProfile) {
	if (!activeFitnessProfile?.intake_required) return null;
	return `/fitness-profiles/${activeFitnessProfile.id}/complete-intake`;
}

export const AuthProvider = ({ children, endpoints }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [
		authenticated,
		setAuthenticated
	] = useState(false);
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		csrfToken,
		setCsrfToken
	] = useState('');
	const [
		user,
		setUser
	] = useState(null);
	const [
		subscription,
		setSubscription
	] = useState({});
	const [
		rosterCount,
		setRosterCount
	] = useState(0);
	const [
		activeFitnessProfile,
		setActiveFitnessProfile
	] = useState(null);
	const [
		fitnessHubsOnly,
		setFitnessHubsOnly
	] = useState(false);
	const [
		showPricingModal,
		setShowPricingModal
	] = useState(false);
	const csrfTokenRef = useRef('');

	const openPricingModal = useCallback(() => {
		setShowPricingModal(true);
	}, []);

	const closePricingModal = useCallback(() => {
		setShowPricingModal(false);
	}, []);

	useEffect(() => {
		csrfTokenRef.current = csrfToken;
	}, [csrfToken]);

	const invalidateSession = useCallback(
		async (reason) => {
			try {
				await fetch(endpoints.sign_out_endpoint, {
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfTokenRef.current
					},
					credentials: 'same-origin'
				});
			} catch (error) {
				console.error('Error clearing expired session:', error);
			}

			setAuthenticated(false);
			setUser(null);
			setActiveFitnessProfile(null);
			setFitnessHubsOnly(false);
			setCsrfToken('');

			const params = new URLSearchParams({ session: 'expired' });
			const returnTo = returnToFromSearch(window.location.search) || window.location.pathname;
			if (returnTo && returnTo !== '/signin') {
				params.set('returnTo', returnTo);
			}
			if (reason) {
				params.set('message', reason);
			}
			window.location.href = `/signin?${params.toString()}`;
		},
		[endpoints.sign_out_endpoint]
	);

	useEffect(() => {
		registerSessionInvalidHandler(invalidateSession);
		return () => registerSessionInvalidHandler(null);
	}, [invalidateSession]);

	const refreshCsrfToken = useCallback(async () => {
		const csrfResponse = await fetch('/csrf-token', { credentials: 'same-origin' });
		if (!csrfResponse.ok) return null;

		const csrfData = await csrfResponse.json();
		setCsrfToken(csrfData.csrfToken);
		return csrfData.csrfToken;
	}, []);

	useEffect(
		() => {
			const initializeAuth = async () => {
				try {
					resetSessionInvalidState();
					await refreshCsrfToken();

					const authResponse = await fetch(
						endpoints.check_authentication_endpoint,
						{ credentials: 'same-origin' }
					);
					if (authResponse.status === 401) {
						setAuthenticated(false);
						return;
					}
					if (!authResponse.ok) {
						throw new Error(`HTTP error! Status: ${authResponse.status}`);
					}
					const authData = await authResponse.json();
					setAuthenticated(authData.authenticated);
					setUser(authData.user || null);
					setSubscription(authData.subscription || null);
					const activeProfile = authData.active_fitness_profile || null;
					setActiveFitnessProfile(activeProfile);
					setFitnessHubsOnly(Boolean(authData.fitness_hubs_only));

					const redirectPath = intakeRedirectPath(activeProfile);
					if (redirectPath && !INTAKE_EXEMPT_PATH_PATTERN.test(location.pathname)) {
						navigate(redirectPath, { replace: true });
					}
				} catch (error) {
					console.error('Error initializing authentication:', error);
				} finally {
					setLoading(false);
				}
			};

			initializeAuth();
		},
		[
			endpoints,
			location.pathname,
			navigate,
			refreshCsrfToken
		]
	);

	useEffect(
		() => {
			if (loading || !authenticated) return;

			const syncSessionOnVisible = async () => {
				if (document.visibilityState !== 'visible') return;

				try {
					await refreshCsrfToken();
					const authResponse = await fetch(endpoints.check_authentication_endpoint, {
						credentials: 'same-origin'
					});

					if (authResponse.status === 401) {
						invalidateSession('Your session expired. Please sign in again.');
						return;
					}

					if (!authResponse.ok) return;

					const authData = await authResponse.json();
					if (!authData.authenticated) {
						invalidateSession('Your session expired. Please sign in again.');
						return;
					}

					const activeProfile = authData.active_fitness_profile || null;
					setActiveFitnessProfile(activeProfile);
					setFitnessHubsOnly(Boolean(authData.fitness_hubs_only));

					const redirectPath = intakeRedirectPath(activeProfile);
					if (redirectPath && !INTAKE_EXEMPT_PATH_PATTERN.test(location.pathname)) {
						navigate(redirectPath, { replace: true });
					}
				} catch (error) {
					console.error('Error refreshing session on visibility:', error);
				}
			};

			document.addEventListener('visibilitychange', syncSessionOnVisible);
			return () => document.removeEventListener('visibilitychange', syncSessionOnVisible);
		},
		[
			authenticated,
			endpoints,
			invalidateSession,
			loading,
			location.pathname,
			navigate,
			refreshCsrfToken
		]
	);

	useEffect(
		() => {
			if (loading || !authenticated) return;

			initializePwa({ endpoints, csrfToken, authenticated });
		},
		[
			authenticated,
			csrfToken,
			endpoints,
			loading
		]
	);

	const syncPushSubscription = useCallback(async () => {
		if (!authenticated || !csrfToken || !endpoints) return;

		await initializePwa({ endpoints, csrfToken, authenticated });
	}, [
		authenticated,
		csrfToken,
		endpoints
	]);

	const patchActiveFitnessProfile = useCallback((updates) => {
		setActiveFitnessProfile((prev) => (prev ? { ...prev, ...updates } : prev));
	}, []);

	const refreshSessionPreferences = useCallback(async () => {
		try {
			const authResponse = await fetch(endpoints.check_authentication_endpoint, {
				credentials: 'same-origin'
			});
			if (authResponse.status === 401) {
				invalidateSession('Your session expired. Please sign in again.');
				return;
			}
			if (!authResponse.ok) return;

			const authData = await authResponse.json();
			if (!authData.authenticated) {
				invalidateSession('Your session expired. Please sign in again.');
				return;
			}
			const activeProfile = authData.active_fitness_profile || null;
			setActiveFitnessProfile(activeProfile);
			setFitnessHubsOnly(Boolean(authData.fitness_hubs_only));

			const redirectPath = intakeRedirectPath(activeProfile);
			if (redirectPath && !INTAKE_EXEMPT_PATH_PATTERN.test(location.pathname)) {
				navigate(redirectPath, { replace: true });
			}
		} catch (error) {
			console.error('Error refreshing session preferences:', error);
		}
	}, [endpoints, invalidateSession, location.pathname, navigate]);

	const signIn = async (credentials) => {
		try {
			const response = await fetch(endpoints.sign_in_endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify(credentials)
			});

			if (response.ok) {
				const data = await response.json();
				resetSessionInvalidState();
				setAuthenticated(true);
				setUser(data.user);
				setSubscription(data.subscription);
				setRosterCount(data.roster_count);
				setActiveFitnessProfile(data.active_fitness_profile || null);
				setFitnessHubsOnly(Boolean(data.fitness_hubs_only));
				const returnTo = returnToFromSearch(window.location.search);
				if (returnTo) {
					navigate(returnTo);
				} else if (data.redirect) {
					navigate(data.redirect);
				} else {
					navigate(
						defaultAuthenticatedHomePath(Boolean(data.fitness_hubs_only))
					);
				}
			} else {
				const errorData = await response.json();
				console.error('Error signing in:', errorData);
				return errorData;
			}
		} catch (error) {
			console.error('Error signing in:', error);
			throw error;
		}
	};

	const signOut = async () => {
		try {
			const response = await fetch(endpoints.sign_out_endpoint, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				}
			});

			if (response.ok) {
				resetSessionInvalidState();
				setAuthenticated(false);
				setUser(null);
				setActiveFitnessProfile(null);
				setFitnessHubsOnly(false);
				setCsrfToken('');
				window.location.href = '/signin';
			} else {
				const errorData = await response.json();
				console.error('Error signing out:', errorData);
			}
		} catch (error) {
			console.error('Error signing out:', error);
		}
	};

	if (loading) {
		return (
			<div>
				<LoadingSpinner />
			</div>
		);
	}

	const authContextValue = {
		authenticated,
		signIn,
		signOut,
		csrfToken,
		user,
		subscription,
		rosterCount,
		activeFitnessProfile,
		fitnessHubsOnly,
		setFitnessHubsOnly,
		patchActiveFitnessProfile,
		refreshSessionPreferences,
		syncPushSubscription,
		showPricingModal,
		openPricingModal,
		closePricingModal,
		endpoints
	};

	return (
		<AuthContext.Provider value={authContextValue}>
			{children}
		</AuthContext.Provider>
	);
};
